RivalIQ.module('data', function (data, App, Backbone, Marionette, $, _, object_hash) {

    'use strict';

    const SourceCache = Marionette.Object.extend({
        initialize: function (attrs, deferred) {
            const me = this;
            me.cache = {};

            if (!_.isUndefined(attrs)) {
                me.setCache(attrs, deferred);
            }
        }
    });

    const HolisticSourceCache = SourceCache.extend({
        getCache: function () {
            const me = this;
            return me.deferred;
        },
        setCache: function (attrs, deferred) {
            const me = this;
            me.deferred = deferred;
        }
    });

    const serializers = {
        metrics: function (val) {

            const result = (val ? object_hash(val) : '');
            return result;
        },
        filters: function (val) {

            return [
                '[',
                _.map(val, function (filter) {

                    return [
                        filter.type,
                        filter.dimension,
                        filter.operator,
                        filter.value
                    ].join(',');
                }).join('|'),
                ']'
            ].join('');
        }
    };

    /**
     * Creates a serialized cache key test function.
     * @param {Function} serializer
     * @returns {Function}
     */
    function createSerializedCacheKeyTest(serializer) {

        return function (cacheValue, fetchValue) {

            return (serializer(cacheValue) === serializer(fetchValue));
        };
    }

    const sourceCaches = {};

    const validCompanyDataParams = [
        'focusCompanyId',
        'landscapeId',
        'companySetDescriptor',
        'timePeriod',
        'sources',
        'granularity',
        'companyId',
        'activeSources',
        'timezone'
    ];

    function granularityCacheKeyTest(cacheValue, fetchValue) {
        return (!_.isFinite(cacheValue) || cacheValue >= fetchValue);
    }

    function companySetIsNone(companySetDescriptor) {
        return companySetDescriptor === RivalIQ.state.companySets.none || _.isUndefined(companySetDescriptor);
    }

    function companySetCacheKeyTest(cacheValue, fetchValue, attributes) {
        const granularity = _.get(attributes, 'granularity');
        const cachedIsNone = companySetIsNone(cacheValue);
        const relaxedGranularity = (granularity < RivalIQ.enums.dataGranularity.trends);
        const companySetIrrelevant = attributes.focusCompanyId && (attributes.companyId === attributes.focusCompanyId);
        return companySetIrrelevant ||
            (cachedIsNone && relaxedGranularity) ||
            (cacheValue === fetchValue) ||
            (cachedIsNone && companySetIsNone(fetchValue));
    }

    function sourceTest(cacheValue, fetchValue, attrs) {
        // If we are not fetching trends we can ignore cross channel
        if (attrs.granularity < RivalIQ.enums.dataGranularity.trends) {
            return !_.difference(_.difference(fetchValue, [RivalIQ.enums.sources.CROSS_CHANNEL]), cacheValue).length;
        }

        return !_.difference(fetchValue, cacheValue).length;
    }

    function timePeriodCacheKeyTest(cacheValue, fetchValue, attributes) {
        const granularity = _.get(attributes, 'granularity');
        const anyTimePeriod = (granularity < RivalIQ.enums.dataGranularity.trends);
        return (anyTimePeriod || RivalIQ.data.CacheLayer.keyType.TIMEPERIOD.test(cacheValue, fetchValue, attributes));
    }

    // TODO would be better if we tested if the companyId is in one of the
    // fetched companySets
    function companyIdTest(cacheValue, fetchValue) {
        return _.isUndefined(cacheValue) || (cacheValue === fetchValue);
    }

    // NOTE for the time being we only want to consider the time zone for
    // requests that contain Instagram Insights, as it is the only source that
    // will take action (i.e., we do not want to miss the cache on this key if
    // we aren't loading Instagram Insights in any capacity)
    function timezoneTest(cacheValue, fetchValue, attributes) {
        let isInstagramInsights = false;
        const sources = attributes.sources;
        const channel = RivalIQ.data.channels.get(RivalIQ.enums.sources.INSTAGRAM_INSIGHTS);
        if (_.isArray(sources)) {
            isInstagramInsights = _.some(sources, function (source) {
                return (source === channel.id);
            });
        } else {
            isInstagramInsights = ((channel.get('sourceMask') & sources) === sources);
        }

        return (!isInstagramInsights || (cacheValue === fetchValue));
    }

    const CompanyData = RivalIQ.data.CacheLayer.extend({
        name: 'CompanyData',
        // TODO: this should be extendable by source, as source
        // specific cache keys MUST be specified here given compilation during
        // initialization of the CacheLayer
        cacheKeys: [
            'landscapeId',
            {
                value: 'sources',
                test: sourceTest
            },
            _.defaults({
                test: timePeriodCacheKeyTest
            }, RivalIQ.data.CacheLayer.keyType.TIMEPERIOD),
            {
                value: 'companySetDescriptor',
                test: companySetCacheKeyTest
            },
            {
                value: 'granularity',
                test: granularityCacheKeyTest
            },
            'presenceId',
            'profile',
            'segment',
            'property',
            'account',
            'activeSources',
            {
                value: 'timezone',
                test: timezoneTest
            },
            {
                value: 'companyId',
                test: companyIdTest
            },
            {
                value: 'metrics',
                test: createSerializedCacheKeyTest(serializers.metrics)
            },
            {
                value: 'filters',
                test: createSerializedCacheKeyTest(serializers.filters)
            }
        ],

        process: function (cache, attributes) {
            // Build caches for new sources
            cache.cachedSources = _.reduce(attributes.sources, function (sources, source) {
                if (!sources[source]) {
                    const SourceCache = sourceCaches[source] || HolisticSourceCache;
                    sources[source] = new SourceCache();
                }

                return sources;
            }, cache.cachedSources || {});

            // If get list of sources we need to pull for
            const missing = _.reduce(attributes.sources, function (arr, source) {
                if (!cache.cachedSources[source].getCache(_.omit(attributes, 'sources'))) {
                    arr.push(source);
                }

                return arr;
            }, []);

            if (missing.length) {
                const companyData = cache.collection;

                // Only pull for sources we actually need to refresh
                attributes.sources = missing;

                // Get new data and merge it onto the base data
                const fetch = companyData.fetch(attributes);

                _.each(missing, function (source) {
                    cache.cachedSources[source].setCache(attributes, fetch);
                });
            }

            // Fetch is complete when all requested sources are available
            const caches = _.invokeMap(_.values(_.pick(cache.cachedSources, attributes.sources)), 'getCache', _.omit(attributes, 'sources'));
            return $.when.apply($, caches)
                .then(function () {
                    return cache.collection;
                });
        },

        onAdd: function (attrs, cache) {
            cache.cachedSources = _.reduce(attrs.sources, function (sources, source) {
                const SourceCache = sourceCaches[source] || HolisticSourceCache;
                sources[source] = new SourceCache(attrs, cache.deferred);

                return sources;
            }, {});
        },

        _buildCacheValue: function (attrs) {
            const collection = new RivalIQ.collections.CompanyData();

            return {
                collection: _.extend(collection, attrs),
                attributes: attrs,
                lazy: attrs && attrs.lazy,
                deferred: new $.Deferred()
            };
        },

        _resolveFromMatchingRequest: function (cache, data) {
            cache.data.collection = data;
            cache.data.deferred.resolve(data);
        },

        loadData: function (attributes, cache) {
            return cache.collection.fetch(attributes)
                .then(_.partial(_.identity, cache.collection));
        },

        defaultSources: [
            RivalIQ.enums.sources.TWITTER,
            RivalIQ.enums.sources.FACEBOOK,
            RivalIQ.enums.sources.YOUTUBE,
            RivalIQ.enums.sources.INSTAGRAM,
            RivalIQ.enums.sources.WEB,
            RivalIQ.enums.sources.CROSS_CHANNEL
        ],

        getValidCompanyDataParams: function () {
            return validCompanyDataParams;
        },

        /**
         * Fetches companyData
         * @param [attributes]
         * @param [options]
         */
        fetch: function (attributes, options) {
            const me = this;

            _.defaults(attributes, _.pick(options, 'granularity'), {
                // TODO remove - insights makes multiple req w/o it
                landscapeId: RivalIQ.state.view.get('landscapeId'),
                companySetDescriptor: RivalIQ.state.view.get('companySetDescriptor'),
                activeSources: RivalIQ.enums.sourceMask.ALL,
                sources: me.defaultSources,
                granularity: RivalIQ.enums.dataGranularity.trends
            });

            // if a single source is specified, override sources to point to that.
            if (attributes.source) {
                attributes.sources = [attributes.source];
            } else if (attributes.channel) {
                attributes.sources = [RivalIQ.data.channels.get(attributes.channel).id];
            }

            // TODO let server handle default?
            _.defaults(attributes, {
                timePeriod: RivalIQ.state.timePeriods.getDefinitionByType(RivalIQ.enums.timePeriod.type.TODAY),
                companySetDescriptor: RivalIQ.state.companySets.none
            });

            const additionalAttributes = {};

            // NOTE for facebook insights, we use the metrics to dramatically optimize the work done on the server
            if (_.includes(attributes.sources, RivalIQ.enums.sources.FACEBOOK_INSIGHTS)) {
                additionalAttributes.metrics = _.reduce(attributes.metrics, function (memo, metric) {
                    const model = new RivalIQ.models.MetricDefinition(metric);
                    const resolvedAttributes = model.resolvedAttributes();
                    if (resolvedAttributes.source === RivalIQ.enums.sources.FACEBOOK_INSIGHTS) {
                        memo.push(metric);
                    }
                    return memo;
                }, []);
            }

            attributes = _.extend(_.pick(attributes, me.getValidCompanyDataParams(attributes)), additionalAttributes);

            return me.getOrLoad(attributes, options);
        },

        fetchCrossChannel: function (attributes, options) {
            const me = this;
            return me.fetch(_.omit(attributes, 'channel', 'sources'), options);
        },

        fetchPresences: function (attributes, options) {
            const me = this;
            const sources = RivalIQ.data.channels.forIdentity().pluck('source');
            const attrs = _.extend({
                sources: sources
            }, attributes);
            const granularity = RivalIQ.enums.dataGranularity.presence;
            return me.fetch(attrs, _.defaults({
                granularity: granularity
            }, options));
        },

        set: function (attrs, collection) {
            const me = this;
            const cache = RivalIQ.data.CacheLayer.prototype.set.apply(me, arguments);
            cache.collection = _.extend(collection, attrs);
            return cache;
        }
    });

    data.companyData = new CompanyData();
}, window.object_hash);
